import { Download, Paperclip, ReceiptText, RotateCcw, Search, SearchX, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { formatDayHeading, formatRange, formatTime, rangeForPreset, RANGE_PRESETS, type RangePreset } from '@core/dates';
import { currencyDigits, formatMoney } from '@core/money';

import { useEditor } from '@/components/ExpenseEditor';
import { Button, CategoryBadge, Chip, EmptyState } from '@/components/ui';
import { useApp, useMoney } from '@/lib/hooks';
import { liveCategories } from '@/lib/merge';
import { setExpenseDeleted } from '@/lib/store';
import { PAYMENT_METHODS, type Expense } from '@/lib/types';

const csvCell = (v: string | number | null) => {
  if (v === null) return '';
  let t = String(v);
  if (typeof v === 'string' && /^[=+\-@]/.test(t)) t = `'${t}`; // spreadsheet formula injection
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

export function Expenses() {
  const { data } = useApp();
  const { format } = useMoney();
  const editor = useEditor();
  const [params, setParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(false);
  const q = params.get('q') ?? '';
  const categoryId = params.get('categoryId');
  const payment = params.get('payment');
  const preset = params.get('range') as RangePreset | null;
  const from = preset ? rangeForPreset(preset, new Date()).from : params.get('from');
  const to = preset ? rangeForPreset(preset, new Date()).to : params.get('to');

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return [...data.expenses.values()]
      .filter((e) => (showDeleted ? e.deletedAt : !e.deletedAt))
      .filter((e) => !categoryId || e.categoryId === categoryId)
      .filter((e) => !payment || e.paymentMethodId === payment)
      .filter((e) => (!from || e.expenseDate >= from) && (!to || e.expenseDate <= to))
      .filter((e) => !term || [e.title, e.merchant, e.notes].some((f) => f?.toLowerCase().includes(term)))
      .sort((a, b) => b.expenseDate.localeCompare(a.expenseDate) || (b.expenseTime ?? '').localeCompare(a.expenseTime ?? '') || b.createdAt.localeCompare(a.createdAt));
  }, [data.expenses, q, categoryId, payment, from, to, showDeleted]);

  const total = rows.reduce((s, e) => s + e.amountMinor, 0);
  const byDay = useMemo(() => {
    const groups: { date: string; total: number; items: Expense[] }[] = [];
    for (const e of rows) {
      const last = groups.at(-1);
      if (last?.date === e.expenseDate) {
        last.items.push(e);
        last.total += e.amountMinor;
      } else groups.push({ date: e.expenseDate, total: e.amountMinor, items: [e] });
    }
    return groups;
  }, [rows]);

  const filtered = Boolean(q || categoryId || payment || from || to);
  const now = new Date();

  const exportCsv = () => {
    const header = 'Date,Time,Title,Category,Amount,Currency,Merchant,Payment method,Notes';
    const lines = rows.map((e) =>
      [
        e.expenseDate,
        e.expenseTime,
        e.title,
        data.categories.get(e.categoryId)?.name ?? '',
        (e.amountMinor / 10 ** currencyDigits(e.currency)).toFixed(currencyDigits(e.currency)),
        e.currency,
        e.merchant,
        PAYMENT_METHODS.find((p) => p.id === e.paymentMethodId)?.name ?? null,
        e.notes,
      ]
        .map(csvCell)
        .join(','),
    );
    const blob = new Blob([`﻿${[header, ...lines].join('\r\n')}\r\n`], { type: 'text/csv' });
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `spendly-expenses-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{showDeleted ? 'Recently deleted' : 'Expenses'}</h1>
          {rows.length > 0 && (
            <div className="row" style={{ marginTop: 6, gap: 8, alignItems: 'baseline' }}>
              <strong className="num" style={{ fontSize: 20 }}>{format(total)}</strong>
              <span className="muted">
                {rows.length} {rows.length === 1 ? 'expense' : 'expenses'}
                {filtered ? ' · filtered' : ''}
              </span>
            </div>
          )}
        </div>
        <div className="row wrap">
          <Button variant="secondary" icon={Download} onClick={exportCsv} disabled={!rows.length}>
            Export CSV
          </Button>
          <Button variant={showDeleted ? 'primary' : 'secondary'} icon={RotateCcw} onClick={() => setShowDeleted((v) => !v)}>
            {showDeleted ? 'Back to expenses' : 'Deleted'}
          </Button>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="grid" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={18} className="faint" style={{ position: 'absolute', left: 12, top: 13 }} />
              <input
                className="input"
                style={{ paddingLeft: 38 }}
                placeholder="Search title, merchant, notes"
                value={q}
                onChange={(e) => update({ q: e.target.value || null })}
                aria-label="Search expenses"
              />
            </div>
            <select className="select" style={{ width: 170 }} value={preset ?? (from || to ? 'custom' : '')} onChange={(e) => update({ range: e.target.value || null, from: null, to: null })} aria-label="Date range">
              <option value="">All time</option>
              {RANGE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
              {(from || to) && !preset && <option value="custom">{formatRange({ from: from ?? to!, to: to ?? from! })}</option>}
            </select>
            <select className="select" style={{ width: 160 }} value={payment ?? ''} onChange={(e) => update({ payment: e.target.value || null })} aria-label="Payment method">
              <option value="">Any payment</option>
              {PAYMENT_METHODS.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="chips">
            <Chip on={!categoryId} onClick={() => update({ categoryId: null })}>All</Chip>
            {liveCategories(data).filter((c) => !c.isArchived).map((c) => (
              <Chip key={c.id} on={categoryId === c.id} onClick={() => update({ categoryId: categoryId === c.id ? null : c.id })}>
                {c.name}
              </Chip>
            ))}
            {filtered && (
              <Button variant="ghost" icon={X} onClick={() => setParams({}, { replace: true })}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          filtered || showDeleted ? (
            <EmptyState icon={SearchX} title={showDeleted ? 'Nothing deleted' : 'No matching expenses'} text={showDeleted ? 'Deleted expenses appear here.' : 'Try a different search or clear the filters.'} />
          ) : (
            <EmptyState icon={ReceiptText} title="No expenses yet" text="Start tracking your spending by adding your first expense." action={<Button onClick={() => editor.open()}>Add expense</Button>} />
          )
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Expense</th>
                <th>Category</th>
                <th className="hide-sm">Payment</th>
                <th className="right">Amount</th>
                {showDeleted && <th />}
              </tr>
            </thead>
            <tbody>
              {byDay.map((g) => [
                <tr key={`h-${g.date}`} className="day-head">
                  <td colSpan={3}>{formatDayHeading(g.date, now)}</td>
                  <td className="right num">{format(g.total)}</td>
                  {showDeleted && <td />}
                </tr>,
                ...g.items.map((e) => {
                  const c = data.categories.get(e.categoryId);
                  return (
                    <tr key={e.id} className={showDeleted ? '' : 'click'} onClick={() => !showDeleted && editor.open(e.id)}>
                      <td>
                        <div className="row" style={{ gap: 12 }}>
                          <CategoryBadge icon={c?.icon ?? 'circle-ellipsis'} color={c?.color ?? 'slate'} size={32} />
                          <div style={{ minWidth: 0 }}>
                            <div className="ellipsis" style={{ fontWeight: 600 }}>{e.title || c?.name}</div>
                            <div className="muted ellipsis" style={{ fontSize: 13 }}>
                              {[e.merchant, e.expenseTime ? formatTime(e.expenseTime) : null].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          {data.receipts.has(e.id) && <Paperclip size={14} className="faint" aria-label="Has receipt" />}
                        </div>
                      </td>
                      <td className="muted">{c?.name}</td>
                      <td className="muted hide-sm">{PAYMENT_METHODS.find((p) => p.id === e.paymentMethodId)?.name ?? '—'}</td>
                      <td className="right num" style={{ fontWeight: 600 }}>{formatMoney(e.amountMinor, e.currency)}</td>
                      {showDeleted && (
                        <td className="right">
                          <Button variant="ghost" icon={RotateCcw} onClick={() => setExpenseDeleted(e.id, false)}>Restore</Button>
                        </td>
                      )}
                    </tr>
                  );
                }),
              ])}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
